// Shared lifecycle contracts for the catalog reference entities — Artist,
// Album, Record Label — brought to parity with the Song Library (owner
// 2026-07-25: "same exact flow, same modal, soft delete = archive and hard
// delete = purge but keep the tombstone in audit history").
//
// The Song Library's own types live in features/tracks/tracksApi.ts. These
// mirror that contract for entities whose permanent delete removes the
// canonical row and retains the append-only audit event as the tombstone.

import type { EntityRefKind } from "@/lib/entityRef";

/** Typed confirmation token — identical to the Song Library. */
export const DELETE_CONFIRM_TOKEN = "DELETE";

export type CatalogEntityKind =
  | "artist"
  | "album"
  | "record-label"
  | "genre"
  | "subgenre";

/**
 * Lifecycle view. Artists and Record Labels spell their archived state
 * `deleted` on the wire; Albums spell it `archived`. The UI always says
 * "Archived" — see catalogEntityCopy.archivedApiStatus for the mapping.
 */
export type CatalogLifecycleView = "active" | "archived" | "all";

export interface CatalogPurgeBlocker {
  code: string;
  message: string;
}

/**
 * Pre-labelled count line. The backend labels these so one modal renders
 * every entity without entity-specific branches.
 */
export interface CatalogPurgeCount {
  label: string;
  value: number;
}

export interface CatalogReferencingSong {
  trackId?: string;
  title?: string | null;
  status?: string | null;
}

export interface CatalogPurgeDependencyCheck {
  checkStatus: string;
  livingSongCount: number;
  associatedTrackCount: number;
  message: string | null;
}

/** Dry-run summary returned by POST /{entity}/{id}/permanent-delete/preflight. */
export interface CatalogPurgePreflight {
  kind: CatalogEntityKind;
  id: string;
  name: string;
  status: string;
  dryRun: boolean;
  eligible: boolean;
  alreadyDeleted?: boolean;
  blockers: CatalogPurgeBlocker[];
  counts: CatalogPurgeCount[];
  dependencyCheck?: CatalogPurgeDependencyCheck | null;
  referencingSongs?: CatalogReferencingSong[];
  songLibraryPath?: string | null;
  willRemove: string[];
  willRetain: string[];
}

export interface CatalogBulkPurgeResultRow {
  id: string;
  name: string | null;
  ok: boolean;
  skipped?: boolean;
  message?: string;
  retryable?: boolean;
  idempotent?: boolean;
}

export interface CatalogBulkPurgeResponse {
  action: string;
  kind: CatalogEntityKind;
  succeeded: number;
  failed: number;
  skipped: number;
  results: CatalogBulkPurgeResultRow[];
}

export interface CatalogPurgeFailure {
  id: string;
  name: string | null;
  message: string;
  retryable: boolean;
}

/** Failure rows for the shared failures table (skipped rows included). */
export function catalogBulkPurgeFailuresFromResponse(
  res: CatalogBulkPurgeResponse,
): CatalogPurgeFailure[] {
  return (res.results || [])
    .filter((row) => !row.ok)
    .map((row) => ({
      id: row.id,
      name: row.name ?? null,
      message: row.message || "Permanent delete failed",
      retryable: row.retryable === true,
    }));
}

export interface CatalogEntityCopy {
  kind: CatalogEntityKind;
  /** Lower-case singular, used mid-sentence: "the record label moves to…". */
  noun: string;
  /** Lower-case plural: "3 record labels". */
  nounPlural: string;
  /** Title-case singular for labels and headings. */
  Noun: string;
  /** The list surface name, e.g. "Record Labels". */
  listName: string;
  /** Short reference kind for EntityRefBadge. */
  refKind: EntityRefKind;
  /** Wire value of the archived state for this entity. */
  archivedApiStatus: "archived" | "deleted";
}
